// ---------------------------------------------------------------------------
// VideoBackground Renderer (editor key: VideoBackground)
// ---------------------------------------------------------------------------
// Hero-style section that loops a muted background video behind a heading +
// subtitle. Published markup is static: a `<video>` element with `autoplay
// muted loop playsinline` (browser-allowed because the audio track is muted),
// a semi-transparent overlay tinted by `overlayOpacity` (0-100), and the copy
// stacked on top. If no `videoUrl` is supplied we emit only the gradient
// fallback so the section still has presence. No client JS is required.

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text, url } from "../escape.js";

interface VideoBackgroundProps {
  videoUrl?: string;
  heading?: string;
  subtitle?: string;
  overlayOpacity?: number | string;
}

const DEFAULT_OPACITY = 60;
const MIN_OPACITY = 0;
const MAX_OPACITY = 100;

function clampOpacity(value: unknown): number {
  const numeric = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(numeric)) return DEFAULT_OPACITY;
  if (numeric < MIN_OPACITY) return MIN_OPACITY;
  if (numeric > MAX_OPACITY) return MAX_OPACITY;
  return Math.round(numeric);
}

function isDirectVideoUrl(rawUrl: string): boolean {
  try {
    const parsed = new URL(rawUrl);
    if (parsed.protocol !== "https:") return false;
    return /\.(mp4|webm|ogg|mov)$/i.test(parsed.pathname);
  } catch {
    return false;
  }
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as VideoBackgroundProps;

  const videoUrl = typeof p.videoUrl === "string" ? p.videoUrl.trim() : "";
  const heading = typeof p.heading === "string" ? p.heading.trim() : "";
  const subtitle = typeof p.subtitle === "string" ? p.subtitle.trim() : "";
  const opacity = clampOpacity(p.overlayOpacity);
  const overlayAlpha = (opacity / 100).toFixed(2);

  const hasDirectVideo = videoUrl.length > 0 && isDirectVideoUrl(videoUrl);
  const videoLayer = hasDirectVideo
    ? `\n    <video src="${url(videoUrl)}" autoplay muted loop playsinline preload="metadata" aria-hidden="true" style="position:absolute;inset:0;width:100%;height:100%;object-fit:cover"></video>`
    : `\n    <div class="ease-video-background__fallback" aria-hidden="true" style="position:absolute;inset:0;background:linear-gradient(135deg,var(--color-primary,#1040cc),var(--color-muted,#e5e7eb))"></div>`;

  const headingHtml = heading
    ? `\n      <h2 class="ease-video-background__heading">${text(heading)}</h2>`
    : "";
  const subtitleHtml = subtitle
    ? `\n      <p class="ease-video-background__subtitle">${text(subtitle)}</p>`
    : "";

  return `<section class="ease-section ease-video-background" style="position:relative;min-height:400px;display:flex;align-items:center;justify-content:center;overflow:hidden;border-radius:12px">${videoLayer}
    <div class="ease-video-background__overlay" aria-hidden="true" style="position:absolute;inset:0;background:rgba(0,0,0,${attr(overlayAlpha)})"></div>
    <div class="ease-video-background__content" style="position:relative;z-index:1;text-align:center;color:#ffffff;padding:48px 24px;max-width:720px">${headingHtml}${subtitleHtml}
    </div>
</section>`;
}

registerRenderer("video-background", { render });
